// React & Routing
import React, { ReactNode, useContext, useEffect } from "react";
import { Route, RouteComponentProps, Switch, useHistory, useLocation } from "react-router-dom";

// Core
import { PrivateRoute } from "Core/Utils";
import { Error2 } from "Core/Views/Error2";
import { NotFound } from "Core/Views/NotFound";

// Custom
import { PageTitle } from "Custom/Components/PageTitle";
import { Client } from "Custom/Globals/AppUrls";
import { Stores, StoresContext } from "Custom/Stores";
import { RouteHelper } from "Custom/Utils";

// Custom Views (Dashboard and Subviews)
import { Dashboard } from "Custom/Views/Dashboard";
import { ViewSnag } from "Custom/Views/DashboardSubViews/ViewSnag";
import { Snags } from "Custom/Views/DashboardSubViews/Snags";
import { Documents } from "Custom/Views/DashboardSubViews/Documents";
import { ChoicesArea } from "Custom/Views/DashboardSubViews/ChoicesArea";
import { Progress } from "Custom/Views/DashboardSubViews/Progress";
import { Inspection } from "Custom/Views/DashboardSubViews/Inspection";
import { PropertyDetails } from "Custom/Views/DashboardSubViews/PropertyDetails";
import { PaymentSection } from "Custom/Views/DashboardSubViews/PaymentSection";
import ChoicesProductGroup from "Custom/Views/DashboardSubViews/ChoicesProductGroup";
import { Choices } from "Custom/Views/DashboardSubViews/Choices";

// Authentication and Session
import { ForgotPasswordPage } from "Custom/Views/ForgotPasswordPage";
import { LoginPage } from "Custom/Views/LoginPage";
import { Logout } from "Custom/Views/Logout";
import { ResetPasswordPage } from "Custom/Views/ResetPasswordPage";
import { SessionExpired } from "Custom/Views/SessionExpired";

// Page Layout
import { PageMenu } from "Custom/Views/PageMenu";
import { SuperAdminView } from "Custom/Views/SuperAdminView";
import { ViewReservationForm } from "Custom/Views/DashboardSubViews/ViewReservationForm";
import { SignaturesSection } from "Custom/Views/DashboardSubViews/SignaturesSection";
import { SignatureRequestPage } from "Custom/Views/SignatureRequestPage";

interface RouteWithPageMenuProps extends RouteComponentProps {
    children: ReactNode;
}

const RouteWithPageMenu: React.FC<RouteWithPageMenuProps> = ({ children }) => (
    <React.Fragment>
        <PageMenu />
        {children}
    </React.Fragment>
);

export const AppRoutes: React.FC = () => {
    const store = useContext<Stores>(StoresContext);
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        if (location.pathname === RouteHelper.ClientRoutes.root()) {
            if (store.domain.AccountStore.IsLoggedIn) {
                const propertyId = store.domain.AccountStore.PropertyId;

                // Check if user is Super Admin (no propertyId means Super Admin)
                if (!propertyId && store.domain.AccountStore.hasSuperAdminPermission) {
                    history.push(RouteHelper.ClientRoutes.SuperAdmin());
                } else if (propertyId) {
                    history.push(RouteHelper.ClientRoutes.Property(propertyId));
                } else {
                    // Fallback to login if no propertyId and not Super Admin
                    history.push(RouteHelper.ClientRoutes.login());
                }
            } else {
                history.push(RouteHelper.ClientRoutes.login());
            }
        }
    }, [location.pathname, store.domain.AccountStore.IsLoggedIn, history, store.domain.AccountStore.PropertyId]);

    return (
        <Switch>
            <Route path={Client.Path.Account.Login} exact component={LoginPage} />
            <Route path={Client.Path.Account.ForgotPassword} exact component={ForgotPasswordPage} />
            <Route
                path={Client.Path.Account.ResetPassword.concat(Client.Path.Account.CreatePassword)}
                component={ResetPasswordPage}
            />
            <Route path={Client.Path.Account.Logout} exact component={Logout} />

            <Route path="/sessionexpired" exact component={SessionExpired} />
            <Route path="/error" exact component={Error2} />
            <Route path="/notfound" exact component={NotFound} />

            {/* Signature request route - accessible via email links */}
            <Route path={Client.Path.SignatureRequest} exact component={SignatureRequestPage} />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Property}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Property" />
                        <PropertyDetails key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />
            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Inspection}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Build" />
                        <Inspection key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />
            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Documents}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Documents" />
                        <Documents key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />
            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Progress}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Progress" />
                        <Progress key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />
            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Choices}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Choices" />
                        <Choices key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.ChoicesArea}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Choices" />
                        <ChoicesArea propertyId={routeProps.match.params.propertyId} areaId={routeProps.match.params.areaId} stores={store} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.ChoicesAreaProductGroup}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Choices" />
                        <ChoicesProductGroup
                            propertyId={routeProps.match.params.propertyId}
                            areaId={routeProps.match.params.areaId}
                            productGroupId={routeProps.match.params.productGroupId}
                            stores={store}
                        />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Snags}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Snags" />
                        <Snags key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Snag}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Snag" />
                        <ViewSnag key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Payment}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Payment" />
                        <PaymentSection key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Signatures}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Signatures" />
                        <SignaturesSection propertyId={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.SignatureForm}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Reservation Form" />
                        <ViewReservationForm
                            key={routeProps.match.params.reservationId}
                            propertyId={routeProps.match.params.propertyId}
                            reservationId={routeProps.match.params.reservationId}
                        />
                    </RouteWithPageMenu>
                )}
            />

            {/* SuperAdmin route must come before Dashboard route to avoid conflict with /:propertyId pattern */}
            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn && store.domain.AccountStore.hasSuperAdminPermission}
                path={Client.Path.SuperAdmin}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Super Admin" />
                        <SuperAdminView />
                    </RouteWithPageMenu>
                )}
            />

            <PrivateRoute
                exact
                isAllowed={() => store.domain.AccountStore.IsLoggedIn}
                path={Client.Path.Dashboard}
                render={routeProps => (
                    <RouteWithPageMenu history={routeProps.history} location={routeProps.location} match={routeProps.match}>
                        <PageTitle title="Dashboard" />
                        <Dashboard key={routeProps.match.params.propertyId} />
                    </RouteWithPageMenu>
                )}
            />
        </Switch>
    );
};
